import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output,
  SimpleChanges, ViewChild
} from '@angular/core';
import { SubSink } from 'subsink';
import { FilePreviewComponentOutput, IMAGE_FILE_EXTENSIONS, IRequestedFile, PDF_FILE_EXTENSIONS, PREVIEW_BYTE_MAX, SUPPORTED_FILE_TYPES, TEXT_FILE_EXTENSIONS } from './file-preview.interface';
import { ProgressComponent } from '@bssh/comp-lib';

@Component({
  selector: 'app-file-preview',
  templateUrl: './file-preview.component.html',
  styleUrls: ['./file-preview.component.scss']
})
export class FilePreviewComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() public apiBaseUrl: string; // baseUrl to feth the data
  @Input() public requestedFile: IRequestedFile = null; // content requested to preview/download
  /**
   * This component determines if a text file is previewable by checking file extension.
   * This input flag allows us to override that logic to preview files that don't meet file extension criteria.
   */
  @Input() public isPreviewableTextFile: boolean;

  @Output() public data: EventEmitter<FilePreviewComponentOutput> = new EventEmitter();

  @ViewChild('filePreviewProgressBar', { static: false }) public filePreviewProgressBar: ProgressComponent;

  protected subs: SubSink = new SubSink();
  public isLoading: boolean = true;
  public errorMessage: string = null;
  public ALL_SUPPORTED_FILE_TYPES = SUPPORTED_FILE_TYPES;
  public fileType: SUPPORTED_FILE_TYPES = null;
  public downloadLink: string = null;
  public hrefUrl: string = null;
  public hrefContent: string = null;
  public PREVIEW_BYTE_MAX_LIMIT = PREVIEW_BYTE_MAX;
  public readonly FILE_TOO_LARGE_ALERT = 'This file is too large for a preview';

  constructor(private http: HttpClient) { }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngAfterViewInit() {
    this.createFilePreviewAndDownload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.requestedFile && changes.requestedFile.currentValue) {
      if (changes.isPreviewableTextFile && changes.isPreviewableTextFile.currentValue) {
        this.setFileType(changes.requestedFile.currentValue.extension, changes.isPreviewableTextFile.currentValue);
      } else {
        this.setFileType(changes.requestedFile.currentValue.extension);
      }
      if (!changes.requestedFile.isFirstChange()) { // using first change to let loader component get initialized
        this.createFilePreviewAndDownload();
      }
    }
  }

  createFilePreviewAndDownload() {
    if (!this.requestedFile) {
      return;
    }
    this.startLoading();
    this.createDownloadLink();
    if (this.requestedFile.Size < this.PREVIEW_BYTE_MAX_LIMIT) {
      this.fetchFileContent();
    } else {
      this.stopLoading();
    }
  }

  private startLoading() {
    this.isLoading = true;
    this.filePreviewProgressBar.start();
  }

  private stopLoading() {
    this.filePreviewProgressBar.complete();
    this.isLoading = false;
  }

  private setFileType(extension: string, isPreviewableTextFile?: boolean): void {

    if (TEXT_FILE_EXTENSIONS.indexOf(extension) > -1 || isPreviewableTextFile === true) {
      this.fileType = this.ALL_SUPPORTED_FILE_TYPES.TEXT_FILE;
    } else if (IMAGE_FILE_EXTENSIONS.indexOf(extension) > -1) {
      this.fileType = this.ALL_SUPPORTED_FILE_TYPES.IMAGE_FILE;
    } else if (PDF_FILE_EXTENSIONS.indexOf(extension) > -1) {
      this.fileType = this.ALL_SUPPORTED_FILE_TYPES.PDF_FILE;
    }
  }

  private createDownloadLink(): void {
    // empty string is also evaluated as null
    if (this.apiBaseUrl === null || this.apiBaseUrl === undefined || !this.requestedFile) {
      this.downloadLink = null;
      return;
    }

    this.updateBaseUrl();

    this.downloadLink = this.apiBaseUrl + this.requestedFile.HrefContent + "?responsecontentdisposition=attachment";
    if (this.downloadLink) {
      // using promise to defer event emission till component(parent/wrapper) is completely mounted
      Promise.resolve(null).then(() => this.data.emit({ downloadLink: this.downloadLink }));
    }
  }

  private fetchFileContent() {
    // empty string is also evaluated as null
    if (this.apiBaseUrl === null || this.apiBaseUrl === undefined || !this.requestedFile) {
      return;
    }

    this.updateBaseUrl();

    // proxy to prevent s3 redirect where we lose the CORS header
    // When we add the proxy param to PDF content URL it forces automatic download and doesn't render in browser correctly so we don't add proxy param for PDFs (BASE-86355)
    const contentUrl = this.apiBaseUrl + this.requestedFile.HrefContent + (this.fileType !== this.ALL_SUPPORTED_FILE_TYPES.PDF_FILE ? '?redirect=proxy' : '');

    // PDF / image files are loaded inline on the template
    if (this.fileType === this.ALL_SUPPORTED_FILE_TYPES.PDF_FILE || this.fileType === this.ALL_SUPPORTED_FILE_TYPES.IMAGE_FILE) {
      this.hrefUrl = contentUrl;
      this.stopLoading();
    } else { // for other file types
      const request = this.http.get(contentUrl, { withCredentials: true, responseType: 'text' });
      this.subs.sink = request.subscribe({
        next: (response: any) => {
          const responseData = response;
          this.hrefContent = responseData; // .toString();
          this.stopLoading();
        },
        error: (err) => {
          this.errorMessage = "Error loading preview";
          this.stopLoading();
        }
      });

    }
  }

  private updateBaseUrl() {
    if (this.requestedFile.HrefContent.indexOf('https') > -1 || this.requestedFile.HrefContent.indexOf('http') > -1) {
      this.apiBaseUrl = '';
    }
  }

}

